import {createEffect, createSignal, on} from 'solid-js'
import {useDispatch, useSelector} from '../../store'
import {useConfig} from '../config/useConfig'
import {useI18n} from '../../i18n'
import {getErrorMessage} from './ErrorMessages'
import {popNotification} from '@te_react/volt-store/lib/actions'
import {logout, clearOauthData, getBackendDeviceList, forceReLogin} from '@te_react/volt-store/lib/actions'
import {useRedux} from '../../store/components/useRedux'
import {useToast} from '../toast/useToast'
import {ToastType} from '../toast/Toast'
import {removeJumpKeys} from '../../utils/removeJumpKeys'

export interface ErrorHandlingProps {
    children?: any
    ignoredNotification: string[]
    errorCodes: Record<string, any>
    voltApi?: any
}

const MAX_DEVICE_REACHED_CONDITIONS = [
    'MAX_DEVICES_REACHED, , NAV-212-HUA, ',
    'MAX_DEVICES_REACHED, FORCE_LOGIN_PENDING_FAILED, NAV-212-HUA, ',
]

const RECORDINGS_BOOT_REQUEST_ERRORS = [
    "RECORDING_NO_QUOTA_SUBSCRIPTION, GET_STORAGE_FAILED, NAV-823-NAG, ",
    "RECORDING_NO_QUOTA_SUBSCRIPTION, GET_FAVORITE_RECORDINGS_FAILED, NAV-823-NAG, "
]

const RECORDINGS_NO_QUOTA_ERRORS = [
    "RECORDING_NO_QUOTA_SUBSCRIPTION, GET_EVENT_RECORDINGS_FAILED, NAV-823-NAG, ",
]

export const ErrorHandling = (props: ErrorHandlingProps) => {
    const {api} = useRedux()
    const toast = useToast()
    const {getConfig} = useConfig()
    const {getI18n} = useI18n()

    const notificationsLength = useSelector((s: any) => s.notifications?.queue.length)
    const notifications = useSelector((s: any) => s.notifications?.queue)
    const navigationReady = useSelector((state:any) => state?.navigation?.ready)
    const [shownNotificationsIds, setShownNotificationsIds] = createSignal<any[]>([])
    const dispatch = useDispatch()

    createEffect(
        on([() => notifications(), () => notificationsLength()], () => {
            notifications().forEach((n: any) => {
                const tag = Array.isArray(n.message?.tag) ? n.message?.tag : [n.message?.tag]
                const notificationMsg = tag.join(', ')

                // Ignore notifications with matching tags
                if (tag.some((t: string) => props.ignoredNotification.includes(t)) || props.ignoredNotification.includes(notificationMsg)
            ) {
                    dispatch(popNotification())
                    return
                }

                //skip if reminder
                if(n.type === 'reminder') return

                //skip recording errors while reload the app
                if (
                    (RECORDINGS_NO_QUOTA_ERRORS.includes(notificationMsg) && !navigationReady()) ||
                    RECORDINGS_BOOT_REQUEST_ERRORS.includes(notificationMsg)
                )
                    return

                // Skip already shown notifications
                if (shownNotificationsIds().includes(n.id)) return

                const customMessage = getConfig().customErrorMsg[tag[0]]
                const title = customMessage?.title
                const type = n.type ?? n.message.type

                let icontype: ToastType | undefined
                if (type === 'failure') {
                    icontype = ToastType.ERROR
                } else if (type === 'information') {
                    icontype = ToastType.INFO
                } else if (type === 'info') {
                    icontype = ToastType.SUCCESS
                }

                // Custom behavior for MAX_DEVICES_REACHED
                if (customMessage?.description && notificationMsg.includes('MAX_DEVICES_REACHED')) {
                    if (getConfig().authenticationType !== 'solo') {
                        customMessage.description = 'MAX_DEVICES_REACHED'
                        if (getConfig().authenticationType === 'oauth') {
                            dispatch(logout({onSuccess: () => {}}))
                        }
                        icontype = ToastType.INFO
                    } else if (MAX_DEVICE_REACHED_CONDITIONS.includes(notificationMsg)) {
                        clearIntervalHeartBeat()
                        dispatch(logout({onSuccess: () => {}}))
                        return
                    } else {
                        dispatch(
                            getBackendDeviceList({
                                includeInactiveDevices: true,
                                username: localStorage.getItem('USER_ID'),
                                deviceTypes: ['1'],
                            })
                        )
                        return
                    }
                    dispatch(clearOauthData())
                }

                // Handle PLB-313 error by triggering re-login
                else if (notificationMsg.includes('PLB-313')) {
                    dispatch(forceReLogin())
                    monitorHeartBeat()
                    return
                }

                // Handle other specific cases
                else if (notificationMsg === 'pinCodeUpdatedSuccess') return

                // Show toast
                toast.show({
                    id: n.id,
                    title: title || '',
                    description: getErrorMessage(getI18n, customMessage?.description ?? notificationMsg, n),
                    subDescription: getSubDescription(n, customMessage, notificationMsg),
                    type: icontype,
                    sticky: false,
                })

                // Mark notification as shown
                setShownNotificationsIds([...shownNotificationsIds(), n.id])

                //User Logout if user is barred/deleted
                if (n.type === 'failure' && n?.message?.tag[0] === 'USER_IS_BARRED') {
                   handleLogout()
                }
            })
        })
    )

    const handleLogout = () => {
        setTimeout(() => {
            if (getConfig()?.jumpLocalKeys) {
                removeJumpKeys(getConfig()?.jumpLocalKeys)
            }
            localStorage.removeItem('TEXT_TRACK')
            localStorage.removeItem('AUDIO_TRACK')
            dispatch(logout({
                onSuccess: () => {
                    dispatch(clearOauthData())
                    window.location.href = '/'
                }
            }))
        }, 1000)
    }

    const clearIntervalHeartBeat = () => {
        clearInterval(heartBeatInterval)
        clearTimeout(heartBeatTimeout)
    }

    let heartBeatInterval: any
    let heartBeatTimeout: any
    const monitorHeartBeat = () => {
        heartBeatInterval = setInterval(() => {
            if (
                api?.platformApi?.authApi?.heartbeatHelper?._deviceLimitReachedRelogin?.code === 'MAX_DEVICES_REACHED'
            ) {
                clearIntervalHeartBeat()
                dispatch(logout({onSuccess: () => {}}))
            }
        }, 1000)

        heartBeatTimeout = setTimeout(() => {
            clearIntervalHeartBeat()
        }, 10000)
    }

    const getSubDescription = (n: any, customMessage: any, notificationMsg: string) => {
        let subDescription: string | undefined
        if (n?.message?.tag?.includes?.('PIN_GENERATION_LIMIT')) {
            subDescription = getI18n()?.formatMessage({id: 'ERROR_GENERATE_OTP_111'})
        } else if (customMessage?.subDescription) {
            subDescription = customMessage.subDescription
        } else {
            const tagDescription = props.errorCodes[n?.message?.tag[0]]?.description
            if (
                tagDescription === 'Exceeded quota' ||
                tagDescription === 'Max Devices Reached' ||
                notificationMsg === 'PURCHASE_FAILED_DUE_EXISTING_ENTITLEMENT, PURCHASE_PRODUCT_FAILED, 708-UFI, '
            ) {
                subDescription = ''
            } else {
                subDescription = tagDescription
            }
        }

        return subDescription
    }

    return <>{props.children}</>
}
